import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { Lock, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff } from "lucide-react";

/**
 * ResetPassword page — consumes the token from the email link and lets
 * the user set a new password.
 *
 * URL: /reset-password?token=<raw_token>
 *
 * On success the backend revokes ALL sessions of this user (defense in
 * depth against compromised accounts), so we redirect to /login with a
 * success notice rather than auto-logging-in. The user re-authenticates
 * with the new password — that confirms the flow worked.
 *
 * Token validity is checked only on submit; the page renders the form
 * even without a token to give a clearer error message on invalid links.
 */
const useToken = () => {
    const location = useLocation();
    return useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("token") || "";
    }, [location.search]);
};

const ResetPassword = () => {
    const navigate = useNavigate();
    const token = useToken();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState("form"); // form | success | invalid
    const [error, setError] = useState("");

    // Password validity hints — same rules as registration.
    const hints = useMemo(() => ({
        length:  password.length >= 8,
        letter:  /[A-Za-z]/.test(password),
        digit:   /\d/.test(password),
        match:   password.length > 0 && password === confirm,
    }), [password, confirm]);

    const canSubmit = hints.length && hints.letter && hints.digit && hints.match && !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        if (!token) {
            setStatus("invalid");
            setError("Посилання неповне — токен відсутній. Запроси новий лист.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setStatus("success");
            // Auto-redirect after 3 seconds.
            setTimeout(() => navigate("/login?reset=success", { replace: true }), 3000);
        } catch (err) {
            const code = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (code === 400 || code === 401) {
                setStatus("invalid");
                setError("Посилання недійсне або вже використане. Запроси новий лист.");
            } else if (code === 422 || code === 409) {
                setError(typeof detail === "string" ? detail : "Пароль не відповідає вимогам.");
            } else if (code === 429) {
                setError("Забагато спроб. Зачекай 15 хвилин.");
            } else {
                setError("Щось пішло не так. Спробуй пізніше.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bg text-default-c relative overflow-hidden">
            <Background />
            <AppHeader />

            <main className="relative z-10 max-w-md mx-auto px-6 pt-24 pb-12">
                <div className="bg-surface/80 backdrop-blur-md border border-border rounded-2xl p-8 shadow-xl">
                    {status === "form" && (
                        <>
                            <div className="text-center mb-6">
                                <Lock className="mx-auto text-primary mb-3" size={36} />
                                <h1 className="text-2xl font-bold mb-2">Новий пароль</h1>
                                <p className="text-sm text-muted-c">
                                    Введи новий пароль для свого AutoAds акаунту.
                                </p>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                data-testid="reset-password-form"
                                className="space-y-4"
                            >
                                <div>
                                    <label className="block text-xs text-muted-c mb-1.5">Новий пароль</label>
                                    <div className="relative">
                                        <input
                                            type={showPwd ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            autoFocus
                                            required
                                            data-testid="reset-password-input"
                                            className="w-full h-10 px-3 pr-10 rounded-md bg-bg border border-border text-sm focus:outline-none focus:border-primary transition"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPwd(v => !v)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-c hover:text-default-c"
                                            tabIndex={-1}
                                        >
                                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>

                                    <div className="flex gap-3 mt-2 text-[11px]">
                                        <span className={hints.length ? "text-emerald-400" : "text-faint-c"}>
                                            8+ символів
                                        </span>
                                        <span className={hints.letter ? "text-emerald-400" : "text-faint-c"}>
                                            буква
                                        </span>
                                        <span className={hints.digit ? "text-emerald-400" : "text-faint-c"}>
                                            цифра
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs text-muted-c mb-1.5">Підтверди пароль</label>
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        data-testid="reset-confirm-input"
                                        className="w-full h-10 px-3 rounded-md bg-bg border border-border text-sm focus:outline-none focus:border-primary transition"
                                    />
                                    {confirm.length > 0 && !hints.match && (
                                        <p className="text-[11px] text-red-400 mt-1">Паролі не співпадають</p>
                                    )}
                                </div>

                                {error && (
                                    <div
                                        data-testid="reset-error"
                                        className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-md p-3"
                                    >
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={!canSubmit}
                                    data-testid="reset-submit"
                                    className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
                                >
                                    {submitting && <Loader2 className="animate-spin" size={16} />}
                                    Встановити пароль
                                </button>
                            </form>
                        </>
                    )}

                    {status === "success" && (
                        <div data-testid="reset-success" className="text-center">
                            <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={40} />
                            <h1 className="text-2xl font-bold mb-2">Пароль оновлено!</h1>
                            <p className="text-sm text-muted-c mb-2">
                                З міркувань безпеки ми вилогінили тебе зі всіх пристроїв.
                            </p>
                            <p className="text-xs text-faint-c mb-6">
                                Переадресовуємо на сторінку входу...
                            </p>
                            <Link
                                to="/login"
                                className="inline-block px-4 py-2 rounded-md bg-primary text-on-primary text-sm hover:bg-primary/90 transition"
                            >
                                Перейти зараз
                            </Link>
                        </div>
                    )}

                    {status === "invalid" && (
                        <div data-testid="reset-invalid" className="text-center">
                            <AlertTriangle className="mx-auto text-amber-400 mb-3" size={40} />
                            <h1 className="text-2xl font-bold mb-2">Посилання недійсне</h1>
                            <p className="text-sm text-muted-c mb-6">
                                {error || "Посилання застаріло або вже використане."}
                            </p>
                            <Link
                                to="/forgot-password"
                                className="inline-block px-4 py-2 rounded-md bg-primary text-on-primary text-sm hover:bg-primary/90 transition"
                            >
                                Запросити новий лист
                            </Link>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default ResetPassword;
